import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, PhoneForwarded, CheckCircle2, Copy } from 'lucide-react';
import { toast } from 'sonner';

const API = '/api';

export default function FollowUpsPage() {
  const navigate = useNavigate();
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchReminders = async () => {
    try {
      const res = await axios.get(`${API}/reminders`, { withCredentials: true });
      const today = new Date().toISOString().split('T')[0];
      const followups = (res.data || []).filter(
        r => (r.auto_type === 'pharma_followup' || r.auto_type === 'account_intro_followup') && r.reminder_date <= today
      );
      setReminders(followups);
    } catch {
      // No follow-ups or API error — just show empty state
      setReminders([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchReminders(); }, []);

  const markDone = async (id) => {
    try {
      await axios.patch(`${API}/reminders/${id}/done`, {}, { withCredentials: true });
      setReminders(prev => prev.filter(r => r.id !== id));
      toast.success('Marked as done');
    } catch {
      toast.error('Failed to update');
    }
  };

  const today = new Date().toISOString().split('T')[0];

  return (
    <div className="p-6 md:p-8 max-w-4xl mx-auto" data-testid="followups-page">
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')} className="text-slate-500 hover:text-slate-700">
          <ArrowLeft className="w-4 h-4 mr-1" /> Dashboard
        </Button>
      </div>

      <div className="flex items-center gap-3 mb-6">
        <PhoneForwarded className="w-6 h-6 text-amber-500" />
        <h1 className="text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Follow-ups for Today
        </h1>
        <Badge variant="secondary" className="text-sm">{reminders.length}</Badge>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-6 h-6 border-2 border-slate-900 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : reminders.length === 0 ? (
        <div className="text-center py-20 flex flex-col items-center">
          <CheckCircle2 className="w-12 h-12 text-emerald-300 mb-3" />
          <p className="text-lg text-slate-500 font-medium">All caught up!</p>
          <p className="text-sm text-slate-400 mt-1">No follow-ups due today.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {reminders.map(r => {
            const daysUntil = Math.ceil((new Date(r.reminder_date + 'T00:00:00') - new Date(today + 'T00:00:00')) / (1000 * 60 * 60 * 24));
            const isOverdue = daysUntil < 0;
            const isAccountIntro = r.auto_type === 'account_intro_followup';
            return (
              <div key={r.id} className={`p-5 rounded-lg border ${isOverdue ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'}`} data-testid={`followup-${r.id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge className={`text-[10px] ${isAccountIntro ? 'bg-indigo-100 text-indigo-700' : 'bg-sky-100 text-sky-700'}`}>
                        {isAccountIntro ? 'Account Intro Check-in' : 'Pharma Follow-up'}
                      </Badge>
                      <Badge className={`text-[10px] ${isOverdue ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                        {isOverdue ? `${Math.abs(daysUntil)}d overdue` : 'Today'}
                      </Badge>
                    </div>
                    <p className="text-sm font-semibold text-slate-900">{r.title}</p>
                    <div className="flex items-center gap-3 mt-2 flex-wrap">
                      {r.contact_name && <span className="text-xs text-slate-600 font-medium">{r.contact_name}</span>}
                      {r.company_name && <span className="text-xs text-slate-400">{r.company_name}</span>}
                    </div>
                    {r.contact_email && (
                      <div className="flex items-center gap-2 mt-2">
                        <span className="text-xs text-slate-500 font-mono bg-white px-2 py-1 rounded border border-slate-200">{r.contact_email}</span>
                        <button
                          onClick={() => { navigator.clipboard.writeText(r.contact_email); toast.success('Email copied!'); }}
                          className="p-1 rounded hover:bg-white/80 transition-colors"
                        >
                          <Copy className="w-3.5 h-3.5 text-slate-400 hover:text-slate-700" />
                        </button>
                      </div>
                    )}
                    {r.share_token && (
                      <button
                        onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/status/${r.share_token}`); toast.success('Status link copied!'); }}
                        className="flex items-center gap-1.5 mt-2 text-xs font-medium text-teal-600 hover:text-teal-800 bg-teal-50 hover:bg-teal-100 px-3 py-1.5 rounded-md transition-colors"
                      >
                        <Copy className="w-3 h-3" /> Copy Status Link
                      </button>
                    )}
                    {r.notes && <p className="text-xs text-slate-500 mt-2">{r.notes}</p>}
                  </div>
                  <button
                    onClick={() => markDone(r.id)}
                    className="text-xs font-medium text-emerald-600 hover:text-emerald-800 bg-emerald-50 hover:bg-emerald-100 px-3 py-1.5 rounded-md transition-colors shrink-0"
                    data-testid={`done-followup-${r.id}`}
                  >
                    Mark Done
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
